import Vue from "vue";
import Vuex from "vuex";

import createPersistedState from "vuex-persistedstate";

import { sortByAttr } from "../common/utils";

Vue.use(Vuex);

const BASE_URL = process.env.VUE_APP_BASE_URL;

const send_request = async (method, partial_url, body = false) => {
  const options = {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    method
  };
  try {
    if (body) {
      options.body = JSON.stringify(body);
    }
    const response = await fetch(`${BASE_URL}${partial_url}`, options);
    const json = await response.json();
    return json;
  } catch (error) {
    throw `Error in request: ${error}`;
  }
};

const get = partial_url => send_request("GET", partial_url);

const post = (partial_url, body = {}) =>
  send_request("POST", partial_url, body);

const put = (partial_url, body = {}) => send_request("PUT", partial_url, body);

const delete_req = partial_url => send_request("DELETE", partial_url);

const parse_and_act_on_response = (
  response,
  errormessage,
  callback = false
) => {
  if ("error" in response) return [false, errormessage];
  if (callback) callback();
  return [true, response];
};

const concat_children_on_property = (arr, property) => {
  return arr.reduce((total, current) => {
    return total.concat(current[property]);
  }, []);
};

const transform_domain_item = item => ({
  id: item.code,
  name: item.naam,
  uuid: item.id
});

const flatten_domain_item = domain_item => ({
  // expects an object in the form: {id: 'id', name: 'name'}
  value: domain_item["uuid"],
  id: domain_item["id"],
  label: domain_item["name"]
});

const filter_on_values = (arr, values, attribute) => {
  return arr.filter(
    item => values.length === 0 || values.includes(item[attribute])
  );
};

const store = new Vuex.Store({
  plugins: [
    createPersistedState({
      storage: window.sessionStorage,
      paths: ["user"]
    })
  ],
  state: {
    backend_data_loaded: false,
    backend_data: {
      topography: [],
      accommodaties: [],
      voorzieningen: [],
      sports: [],
      substrates: [],
      services: [],
      current_accommodatie: null,
      current_voorziening: null,
      voorziening_geometry_cached: false,
      current_sportcomplex: null,
      todolist: [],
      todolist_n_search_items: null,
      downloadlink: null,
      permissions: [],
      roles: [],
      users: []
    },
    user: null,
    search: {
      search_string: "",
      last_verified_range: [],
      provincies: [],
      gemeenten: [],
      plaatsen: [],
      accommodaties: [],
      sporten: [],
      ondergronden: [],
      voorziening_typen: [],
      indoor: [],
      is_finished: false, // for to do
      actor_uuid: [], // for to do can only be a single value
      creator_uuid: [], // for to do can only be a single value
      page: 1
    }
  },
  getters: {
    sports: state => {
      const sports = state.backend_data.sports.map(transform_domain_item);
      return sortByAttr(sports, "name");
    },
    sports_flattened: (_, getters) => getters.sports.map(flatten_domain_item),
    substrates: state => {
      const substrates = state.backend_data.substrates.map(
        transform_domain_item
      );
      return sortByAttr(substrates, "name");
    },
    substrates_flattened: (_, getters) =>
      getters.substrates.map(flatten_domain_item),
    services: state => {
      const services = state.backend_data.services.map(transform_domain_item);
      return sortByAttr(services, "name");
    },
    services_flattened: (_, getters) =>
      getters.services.map(flatten_domain_item),
    current_user: state => state.user,
    provinces: state => {
      const provinces = state.backend_data.topography.map(
        transform_domain_item
      );
      return sortByAttr(provinces, "name");
    },
    municipalities: state => {
      const municipalities = concat_children_on_property(
        state.backend_data.topography,
        "gemeenten"
      ).map(transform_domain_item);
      return sortByAttr(municipalities, "name");
    },
    municipalities_filtered: state => {
      const selected_provinces = filter_on_values(
        state.backend_data.topography,
        state.search.provincies,
        "code"
      );

      const municipalities = concat_children_on_property(
        selected_provinces,
        "gemeenten"
      ).map(transform_domain_item);
      return sortByAttr(municipalities, "name");
    },
    places: state => {
      const municipalities = concat_children_on_property(
        state.backend_data.topography,
        "gemeenten"
      );
      const places = concat_children_on_property(municipalities, "plaatsen");
      return sortByAttr(places.map(transform_domain_item), "name");
    },
    places_filtered: state => {
      const selected_provinces = filter_on_values(
        state.backend_data.topography,
        state.search.provincies,
        "code"
      );
      const municipalities = concat_children_on_property(
        selected_provinces,
        "gemeenten"
      );
      const selected_municipalities = filter_on_values(
        municipalities,
        state.search.gemeenten,
        "code"
      );
      const places = concat_children_on_property(
        selected_municipalities,
        "plaatsen"
      ).map(transform_domain_item);

      return sortByAttr(places, "name");
    },
    users_flattened: state => {
      return state.backend_data.users.map(user => ({
        uuid: user["id"],
        id: user["id"],
        name: user["email"]
      }));
    }
  },
  mutations: {
    ADD_COOKIE(state, session) {
      state.backend_data.session = session;
    },
    ADD_TOPOGRAPHY(state, topography) {
      state.backend_data.topography = topography;
    },
    ADD_FOUND_ACCOMMODATIES(state, accommodaties) {
      state.backend_data.accommodaties = accommodaties;
    },
    ADD_SPORTS(state, sports) {
      state.backend_data.sports = sports;
    },
    ADD_SUBSTRATES(state, substrates) {
      state.backend_data.substrates = substrates;
    },
    ADD_SERVICES(state, services) {
      state.backend_data.services = services;
    },
    SET_CURRENT_USER(state, user) {
      state.user = user;
    },
    ADD_PERMISSIONS(state, permissions) {
      state.backend_data.permissions = permissions;
    },
    ADD_ROLES(state, roles) {
      state.backend_data.roles = roles;
    },
    ADD_ROLE(state, role) {
      state.backend_data.roles.push(role);
    },
    ADD_USERS(state, users) {
      state.backend_data.users = users;
    },
    EMPTY_SPORTCOMPLEX(state) {
      state.backend_data.current_sportcomplex = null;
    },
    LOAD_SPORTCOMPLEX(state, sportcomplex) {
      state.backend_data.current_sportcomplex = sportcomplex;
    },
    LOAD_ACCOMMODATIE(state, accommodatie) {
      state.backend_data.current_accommodatie = accommodatie;
    },
    UPDATE_ACCOMMODATIE(state, updated_accommodatie) {
      state.backend_data.current_accommodatie = updated_accommodatie;
    },
    ADD_VOORZIENING_TO_CACHE(state, voorziening) {
      if (
        state.backend_data.voorzieningen.find(
          vz => vz.id === voorziening.id
        ) === undefined
      ) {
        state.backend_data.voorzieningen.push(voorziening);
      }
    },
    SET_VOORZIENING_GEOMETRY_CACHED(state, id) {
      state.backend_data.voorziening_geometry_cached = id;
    },
    UNSET_VOORZIENING_GEOMETRY_CACHED(state) {
      state.backend_data.voorziening_geometry_cached = null;
    },
    REMOVE_VOORZIENING_FROM_CACHE(state, voorziening_id) {
      const index = state.backend_data.voorzieningen.findIndex(
        vz => vz.id === voorziening_id
      );
      state.backend_data.voorzieningen.splice(index, 1);
    },
    UPDATE_VOORZIENING_IN_CACHE(state, updated_voorziening) {
      const index = state.backend_data.voorzieningen.findIndex(
        vz => vz.id === updated_voorziening.id
      );
      state.backend_data.voorzieningen.splice(index, 1);
      state.backend_data.voorzieningen.push(updated_voorziening);
    },
    LOAD_DOWNLOADLINK(state, downloadlink) {
      state.backend_data.downloadlink = downloadlink;
    },
    SET_BACKEND_DATA_LOADED(state) {
      state.backend_data_loaded = true;
    },
    SET_FILTER_VALUE(state, { selected, filtername }) {
      state.search[filtername] = selected;
    },
    SET_FILTER_VALUE_TODOLIST(state, { selected, filtername }) {
      state.search_todolist[filtername] = selected;
    },
    ADD_FEEDBACK(state, feedback) {
      // TODO: refactor names of feedback to todolist
      state.backend_data.todolist.push(feedback);
    },
    UPDATE_FEEDBACK(state, feedback) {
      const index = state.backend_data.todolist.findIndex(
        fb => fb.id === feedback.id
      );
      state.backend_data.todolist.splice(index, 1);
      state.backend_data.todolist.push(feedback);
    },
    EMPTY_FEEDBACK_FOR_OBJECT_CACHE(state) {
      state.backend_data.todolist = [];
    },
    LOAD_FEEDBACK_FOR_OBJECT(state, feedback_items) {
      if (feedback_items.length === 0) return;
      state.backend_data.todolist = state.backend_data.todolist
        .filter(item => item.object !== feedback_items[0].object)
        .concat(feedback_items);
    },
    DELETE_TODO_ITEM(state, id) {
      state.backend_data.todolist = state.backend_data.todolist.filter(
        item => item.id !== id
      );
    },
    LOAD_TODO_ITEMS(state, todolist_search_results) {
      state.backend_data.todolist = todolist_search_results["results"];
      state.backend_data.todolist_n_search_items =
        todolist_search_results["total_items"];
    }
  },
  actions: {
    async loadBackendData({ dispatch, commit }) {
      await Promise.all([
        dispatch("loadCurrentUser"),
        dispatch("loadTopography"),
        dispatch("loadSports"),
        dispatch("loadSubstrates"),
        dispatch("loadServices")
      ]);
      await dispatch("searchForAccommodaties");
      commit("SET_BACKEND_DATA_LOADED");
    },
    async loadTopography({ commit }) {
      commit("ADD_TOPOGRAPHY", await get("search/filters/bg"));
    },

    async searchForAccommodaties({ commit, state }) {
      commit(
        "ADD_FOUND_ACCOMMODATIES",
        await post("search/search", state.search)
      );
    },
    async exportAccommodaties({ state }, { organisation, reason }) {
      await post("search/export", {
        query: state.search,
        user_id: state.user.id,
        filetype: "excel",
        organisation: organisation,
        reason: reason
      });
    },
    async getDownloadLink({ commit }, id) {
      commit(
        "LOAD_DOWNLOADLINK",
        await get(`search/export/downloadlink/${id}`)
      );
    },
    async loadSports({ commit }) {
      commit("ADD_SPORTS", await get("domeinlijsten/sport_typen/"));
    },
    async loadSubstrates({ commit }) {
      commit("ADD_SUBSTRATES", await get("domeinlijsten/ondergrond_typen/"));
    },
    async loadServices({ commit }) {
      commit("ADD_SERVICES", await get("domeinlijsten/voorziening_typen/"));
    },
    async loadCurrentUser({ commit }) {
      commit("SET_CURRENT_USER", await get("auth/whoami"));
    },
    async loadSportcomplexByAccommodatieId({ commit, state }, id) {
      await this.dispatch("loadAccommodatie", id);
      commit("EMPTY_SPORTCOMPLEX");
      const scid = state.backend_data.current_accommodatie.sportcomplex.id;
      commit("LOAD_SPORTCOMPLEX", await get(`locaties/sportcomplex/${scid}`));
    },
    async loadAccommodatie({ commit, state }, id) {
      // first we load basic accommodation data,
      const base_accommodatie = await get(`locaties/accommodatie/${id}`);
      commit("LOAD_ACCOMMODATIE", base_accommodatie);
      // if all voorzieningen of this accommodatie are already cached, we return
      let cached = true;
      for (const voorziening of base_accommodatie.voorzieningen) {
        if (
          !state.backend_data.voorzieningen
            .map(vz => vz.id)
            .includes(voorziening.id)
        ) {
          cached = false;
        }
      }
      if (cached) return;

      // then we load geometry of voorzieningen
      const full_accommodatie = await get(
        `locaties/accommodatie_with_voorziening_geometry/${id}`
      );
      for (const voorziening of full_accommodatie.voorzieningen) {
        voorziening["accommodatie"] = {};
        voorziening.accommodatie["id"] = id;
        commit("ADD_VOORZIENING_TO_CACHE", voorziening);
      }
    },
    async loadAccommodatiesInDistance(_, id) {
      // this endpoint is pretty slow / too slow if we process loads of accommodaties
      // for instance in the case where accommodaties have the default coordinate
      return await get(`locaties/get_accommodaties_in_distance/${id}/`);
    },
    async updateAccommodatie({ commit, state }, properties) {
      // this can handle updates of various sets of properties
      const current = state.backend_data.current_accommodatie;
      const id = current.id;
      const updated_accommodatie = { ...current, ...properties };
      commit("UPDATE_ACCOMMODATIE", updated_accommodatie);
      let response = await put(
        `locaties/accommodatie/${id}`,
        updated_accommodatie
      );

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen."
      );
    },
    async updateAccommodatieCoordinates({ commit, state }, coordinates) {
      const current = state.backend_data.current_accommodatie;
      const id = current.id;
      await put(`locaties/accommodatie/${id}/coordinates`, coordinates);
      commit("LOAD_ACCOMMODATIE", await get(`locaties/accommodatie/${id}`));
    },
    async moveAccommodatieToSportcomplex(
      { commit, dispatch },
      { accommodatie_id, sportcomplex_id }
    ) {
      const response = await put(
        `locaties/accommodatie/${accommodatie_id}/move_to_sportcomplex/${sportcomplex_id}`
      );

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden. Gegevens zijn niet opgeslagen.",
        async () => {
          commit(
            "LOAD_ACCOMMODATIE",
            await get(`locaties/accommodatie/${accommodatie_id}`)
          );
          await dispatch("loadSportcomplexByAccommodatieId", accommodatie_id);
        }
      );
    },
    async moveAccommodatieToNewSportcomplex({ commit }, { accommodatie_id }) {
      const response = await put(
        `locaties/accommodatie/${accommodatie_id}/move_to_new_sportcomplex/`
      );

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden. Gegevens zijn niet opgeslagen.",
        async () => {
          commit(
            "LOAD_ACCOMMODATIE",
            await get(`locaties/accommodatie/${accommodatie_id}`)
          );
        }
      );
    },
    async geocodeByAddress({ commit, state }) {
      const current = state.backend_data.current_accommodatie;
      const id = current.id;
      await put(`locaties/accommodatie/${id}/geocode_from_address`);
      commit("LOAD_ACCOMMODATIE", await get(`locaties/accommodatie/${id}`));
    },
    async addVerification({ commit, state }, accommodatie_id) {
      const user_id = state.user.id;
      let response = await post(`locaties/accommodatie/verify`, {
        accommodatie_id,
        user_id
      });

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het verifiëren.",
        async () => {
          commit(
            "LOAD_ACCOMMODATIE",
            await get(`locaties/accommodatie/${accommodatie_id}`)
          );
        }
      );
    },
    async addVoorziening({ state, dispatch }, properties) {
      let response = await post(`locaties/voorziening`, properties);
      dispatch("loadAccommodatie", state.backend_data.current_accommodatie.id);

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier."
      );
    },
    async updateVoorziening({ commit, state }, { id, properties }) {
      // this can handle updates of various sets of properties
      const current = state.backend_data.voorzieningen.find(vz => vz.id === id);
      // the datastructure of updated_voorziening is correct for backend, but different from what we need in cache
      const updated_voorziening = { ...current, ...properties };

      updated_voorziening.accommodatie =
        state.backend_data.current_accommodatie;
      let response = await put(
        `locaties/voorziening/${id}`,
        updated_voorziening
      );

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen.",
        async () => {
          commit(
            "UPDATE_VOORZIENING_IN_CACHE",
            await get(`locaties/voorziening/${id}`)
          );
        }
      );
    },
    async updateVoorzieningGeometry({ commit, state }, { id, properties }) {
      const current = state.backend_data.voorzieningen.find(vz => vz.id === id);
      const updated_voorziening = { ...current, ...properties };
      updated_voorziening.accommodatie =
        state.backend_data.current_accommodatie;

      let response = await put(
        `locaties/voorziening/${id}/geometrie`,
        updated_voorziening
      );

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen.",
        async () => {
          commit(
            "UPDATE_VOORZIENING_IN_CACHE",
            await get(`locaties/voorziening/${id}`)
          );
        }
      );
    },
    async deleteVoorziening({ commit }, id) {
      let response = await delete_req(`locaties/voorziening/${id}`);

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het verwijderen van de voorziening. Voorziening is niet verwijderd.",
        () => {
          commit("REMOVE_VOORZIENING_FROM_CACHE", id);
        }
      );
    },
    async updateDomeinlijst({ dispatch }, { name, properties }) {
      const domeinlijst_eng = {
        voorziening_typen: "Services",
        ondergrond_typen: "Substrates",
        sport_typen: "Sports"
      }[name];

      let response = await put(`domeinlijsten/${name}/`, properties);
      dispatch(`load${domeinlijst_eng}`);

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen."
      );
    },
    async addDomeinlijst({ dispatch }, { name, properties }) {
      const domeinlijst_eng = {
        voorziening_typen: "Services",
        ondergrond_typen: "Substrates",
        sport_typen: "Sports"
      }[name];

      let response = await post(`domeinlijsten/${name}/`, properties);
      dispatch(`load${domeinlijst_eng}`);
      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen."
      );
    },
    async replaceDomeinlijst({ dispatch }, { name, id, id_new }) {
      const domeinlijst_eng = {
        voorziening_typen: "Services",
        ondergrond_typen: "Substrates",
        sport_typen: "Sports"
      }[name];

      let response = await delete_req(`domeinlijsten/${name}/${id}/${id_new}`);
      dispatch(`load${domeinlijst_eng}`);

      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen."
      );
    },
    async checkFlexprops({ dispatch }, { id }) {
      const response = await post(
        `domeinlijsten/voorziening_typen/${id}/check_flexprops`
      );
      dispatch("loadServices");
      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen."
      );
    },
    async applyFlexprops({ dispatch }, { id }) {
      const response = await post(
        `domeinlijsten/voorziening_typen/${id}/apply_flexprops`
      );
      dispatch("loadServices");
      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen."
      );
    },
    async update_search(
      { commit, dispatch },
      { selected, filtername, routename }
    ) {
      // 'selected' is a weird name; can also be a search string or date
      if (filtername === "provincies") {
        commit("SET_FILTER_VALUE", { selected: [], filtername: "gemeenten" });
        commit("SET_FILTER_VALUE", { selected: [], filtername: "plaatsen" });
      }
      if (filtername === "gemeenten") {
        commit("SET_FILTER_VALUE", {
          selected: [],
          filtername: "plaatsen"
        });
      }
      if (filtername !== "page") {
        commit("SET_FILTER_VALUE", {
          selected: 1,
          filtername: "page"
        });
      }
      commit("SET_FILTER_VALUE", { selected, filtername });

      if (routename === "TodoSearchResults") {
        await dispatch("searchForTodoItems");
      } else {
        await dispatch("searchForAccommodaties");
      }
    },
    async suggestAccommodation(_, accommodation) {
      return await post("todolist/comment", accommodation);
    },
    async addAccommodation(_, accommodation) {
      return await post(
        "locaties/add_accommodatie_with_voorzieningen/",
        accommodation
      );
    },
    async addFeedback({ commit, getters, dispatch, state }, feedback) {
      commit("ADD_FEEDBACK", {
        ...feedback,
        creator: getters.current_user.id,
        finished: null
      });
      // TODO: add creator in backend, and not here
      let response = await post("todolist/comment", feedback);
      // TODO: add conditional on permission here
      if (state.user.permissions.includes("read todoitem")) {
        dispatch("loadFeedbackByObject", {
          objectname: feedback.object_name,
          id: feedback.object
        });
      }

      if ("error" in response)
        return [
          false,
          "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen."
        ];
      return [true, response];
    },
    async updateFeedback({ commit }, feedback) {
      const response = await put(`todolist/${feedback.id}`, feedback);
      return parse_and_act_on_response(
        response,
        "Er is een fout opgetreden bij het versturen van het formulier. Gegevens zijn niet opgeslagen.",
        () => {
          commit("UPDATE_FEEDBACK", feedback);
        }
      );
    },
    emptyFeedbackForObjectCache({ commit }) {
      commit("EMPTY_FEEDBACK_FOR_OBJECT_CACHE");
    },
    async loadFeedbackByObject({ commit, dispatch }, { objectname, id }) {
      const feedback = await get(`todolist/${objectname}/${id}`);
      dispatch("emptyFeedbackForObjectCache");
      commit("LOAD_FEEDBACK_FOR_OBJECT", feedback);
    },
    async loadFeedbackItem(_, id) {
      return await get(`todolist/${id}`);
    },
    async finishFeedbackItem(_, id) {
      // this action does not update the data in the store
      await put(`todolist/finish/${id}`);
    },
    async deleteFeedbackItem({ commit }, id) {
      commit("DELETE_TODO_ITEM", id);
      await delete_req(`todolist/${id}`);
    },
    async searchForTodoItems({ commit, state }) {
      const search = {
        ...state.search,
        actor_uuid: state.search.actor_uuid[0],
        creator_uuid: state.search.creator_uuid[0]
      };
      commit("LOAD_TODO_ITEMS", await post(`todolist/search`, search));
    },
    async logout() {
      window.sessionStorage.clear();
      await post(`auth/signout`);
    },
    async login({ dispatch }, credentials) {
      let response = await post(`auth/signin`, credentials);
      dispatch("loadCurrentUser");
      return response;
    },
    async registerUser(_, credentials) {
      return await post(`auth/register`, credentials);
    },
    async registerPublicUser(_, credentials) {
      return await post(`auth/register_public_user`, credentials);
    },
    async setPassword(_, credentials) {
      return await post(`auth/setpwd`, credentials);
    },
    requestPassword(_, email) {
      post(`auth/forgot`, email);
    },
    async changePassword(_, credentials) {
      return await post(`auth/changepwd`, credentials);
    },
    async refreshAPIKey() {
      return await get("auth/refresh_api_key");
    },
    async getPlaatsByPostcode(_, postcode) {
      return await get(`bg/plaats/postcode/${postcode}`);
    },
    async loadPermissions({ commit }) {
      commit("ADD_PERMISSIONS", await get("auth/permissions"));
    },
    async updatePermissions({ dispatch, state }, { role_id, permissions }) {
      /* 
      backend_data.permissions: [
        {"name":"read accommodatie","permission_id":1},
        {"name":"create accommodatie","permission_id":2}
      ]
      role_permissions: {1: true, 2: false, 4: true}
      new_permissions: [
          { permission_id: 1, permitted: true },
          { permission_id: 2, permitted: false },
          { permission_id: 3, permitted: false },
          {permission_id: 4, permitted: true}
        ] 
      */
      const checked_permission_ids = Object.entries(permissions)
        .filter(p => p[1] === true)
        .map(p => p[0]) //take the id
        .map(p => parseInt(p));
      permissions = state.backend_data.permissions.map(permission => ({
        ...permission,
        permitted: checked_permission_ids.includes(permission.permission_id)
      }));
      const response = await put("auth/permissions", {
        role_id,
        permissions
      });
      dispatch("loadRoles");
      return response;
    },
    async loadRoles({ commit }) {
      commit("ADD_ROLES", await get("auth/roles"));
    },
    async addRole({ commit }, role) {
      let response = await post("auth/roles", {
        role: role
      });
      if ("id" in response) {
        let new_role = {
          role: role,
          id: response["id"],
          permissions: []
        };
        commit("ADD_ROLE", new_role);
        return new_role;
      }

      return response;
    },
    async loadUsers({ commit }) {
      commit("ADD_USERS", await get("api/internal/admin/users"));
    },
    async ensureUsersAreLoaded({ state, dispatch }) {
      if (state.backend_data.users.length === 0) dispatch("loadUsers");
    },
    async updateUserRoles({ dispatch }, { user_id, role_ids }) {
      const response = await put(
        `api/internal/admin/user/${user_id}/roles`,
        role_ids
      );
      dispatch("loadUsers");
      return response;
    }
  }
});

export default store;
export { get };
